import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Avatar,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  CheckCircle as CheckInIcon,
  Cancel as CheckOutIcon,
  Schedule as DurationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import Swal from "sweetalert2";

const AttendancePayroll = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    checkIn: '',
    checkOut: '',
    notes: ''
  });
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    fromDate: dayjs().startOf("month"),
    toDate: dayjs().endOf("month"),
    employeeId: "",
    status: "all",
  });
  const [employeeList, setEmployeeList] = useState([]);
  const [stats, setStats] = useState({
    totalRecords: 0,
    present: 0,
    absent: 0,
    late: 0,
    totalHours: 0,
  });
  const printRef = useRef();

  // Print functions
  const handlePrintList = () => {
    const printWindow = window.open('', '_blank');
    const printStyles = `
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .header h2 { margin-bottom: 10px; }
        .header p { color: #666; }
        .filters { margin-bottom: 20px; padding: 10px; background: #f5f5f5; border-radius: 5px; }
        .stats { display: flex; justify-content: space-around; margin-bottom: 20px; }
        .stat-item { text-align: center; padding: 10px; }
        .stat-number { font-size: 24px; font-weight: bold; color: #1976d2; }
        .stat-label { font-size: 12px; color: #666; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f5f5f5; font-weight: bold; }
        td img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; }
        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .status-chip { padding: 4px 8px; border-radius: 12px; font-size: 12px; font-weight: bold; }
        .status-ontime { background-color: #e8f5e8; color: #2e7d32; }
        .status-late { background-color: #fff3e0; color: #f57c00; }
        .status-early { background-color: #e3f2fd; color: #1976d2; }
        @media print { body { margin: 10px; } }
      </style>
    `;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Attendance Report</title>
          ${printStyles}
        </head>
        <body>
          <div class="header">
            <h2>ATTENDANCE REPORT</h2>
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>
          
          <div class="filters">
            <strong>Filter Period:</strong> ${filters.fromDate?.format("MMM DD, YYYY") || 'All'} - ${filters.toDate?.format("MMM DD, YYYY") || 'All'}
            ${filters.employeeId ? `<br><strong>Employee ID:</strong> ${filters.employeeId}` : ''}
            ${filters.status !== 'all' ? `<br><strong>Status:</strong> ${filters.status}` : ''}
          </div>
          
          <div class="stats">
            <div class="stat-item">
              <div class="stat-number">${stats.totalRecords}</div>
              <div class="stat-label">Total Records</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">${stats.present}</div>
              <div class="stat-label">On Time</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">${stats.late}</div>
              <div class="stat-label">Late</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">${stats.totalHours.toFixed(1)}</div>
              <div class="stat-label">Total Hours</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Image</th>
                <th>Employee ID</th>
                <th>Employee Name</th>
                <th>Date</th>
                <th>Check In</th>
                <th>Check Out</th>
                <th>Duration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${attendanceData.map(record => {
                const rawImg = record.imageUrl || record.profileImage || record.image || record.employeePhoto || "";
                const base = axios.defaults.baseURL || "";
                const imageUrl = rawImg && rawImg !== base
                  ? (rawImg.startsWith("http") ? rawImg : `${base}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`)
                  : null;
                const statusClass = record.status.toLowerCase().replace(' ', '');
                
                return `
                  <tr>
                    <td class="text-center">
                      ${imageUrl ? 
                        `<img src="${imageUrl}" alt="${record.employeeName || 'Employee'}" onerror="this.style.display='none'">` : 
                        '<div style="width:40px;height:40px;background:#f0f0f0;border-radius:50%;margin:0 auto;"></div>'
                      }
                    </td>
                    <td>${record.employeeId}</td>
                    <td>${record.employeeName}</td>
                    <td>${dayjs(record.date).format("MMM DD, YYYY")}</td>
                    <td>${dayjs(record.checkIn).format("hh:mm A")}</td>
                    <td>${dayjs(record.checkOut).format("hh:mm A")}</td>
                    <td class="text-right">${formatDuration(record.duration)}</td>
                    <td class="text-center">
                      <span class="status-chip status-${statusClass}">
                        ${record.status}
                      </span>
                    </td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <div style="margin-top: 30px; text-align: center;">
            <p><strong>Total Records:</strong> ${attendanceData.length}</p>
            <p style="color: #666; font-size: 12px;">This is a computer generated report</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  const handlePrintSingle = () => {
    if (!selectedRecord) return;
    
    const printWindow = window.open('', '_blank');
    const printStyles = `
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .header { text-align: center; margin-bottom: 30px; }
        .info-section { margin-bottom: 20px; }
        .info-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .info-label { font-weight: bold; min-width: 120px; }
        .info-value { flex: 1; }
        .status-section { padding: 15px; background: #f5f5f5; border-radius: 5px; margin-bottom: 20px; }
        .status-badge { padding: 8px 16px; border-radius: 20px; font-weight: bold; text-align: center; }
        .status-ontime { background-color: #e8f5e8; color: #2e7d32; }
        .status-late { background-color: #fff3e0; color: #f57c00; }
        .status-early { background-color: #e3f2fd; color: #1976d2; }
        @media print { body { margin: 10px; } }
      </style>
    `;
    
    const rawImg = selectedRecord.imageUrl || selectedRecord.profileImage || selectedRecord.image || selectedRecord.employeePhoto || "";
    const base = axios.defaults.baseURL || "";
    const imageUrl = rawImg && rawImg !== base
      ? (rawImg.startsWith("http") ? rawImg : `${base}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`)
      : null;
    const statusClass = selectedRecord.status.toLowerCase().replace(' ', '');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Attendance Details - ${selectedRecord.employeeName}</title>
          ${printStyles}
        </head>
        <body>
          <div class="header">
            <h2>ATTENDANCE DETAILS</h2>
            <p>Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
          </div>
          
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Employee ID:</span>
              <span class="info-value">${selectedRecord.employeeId}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Employee Name:</span>
              <span class="info-value">${selectedRecord.employeeName}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Date:</span>
              <span class="info-value">${dayjs(selectedRecord.date).format("MMM DD, YYYY")}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Check In:</span>
              <span class="info-value">${dayjs(selectedRecord.checkIn).format("hh:mm A")}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Check Out:</span>
              <span class="info-value">${dayjs(selectedRecord.checkOut).format("hh:mm A")}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Duration:</span>
              <span class="info-value">${formatDuration(selectedRecord.duration)}</span>
            </div>
          </div>
          
          <div class="status-section">
            <h3 style="margin-top: 0;">Status</h3>
            <div class="status-badge status-${statusClass}">
              ${selectedRecord.status}
            </div>
          </div>
          
          ${imageUrl ? `
            <div style="text-align: center; margin-top: 20px;">
              <h3>Employee Photo</h3>
              <img src="${imageUrl}" alt="${selectedRecord.employeeName}" 
                   style="width: 120px; height: 120px; border-radius: 50%; object-fit: cover; border: 3px solid #ddd;" />
            </div>
          ` : ''}
          
          <div style="margin-top: 40px; text-align: center;">
            <p style="color: #666;">This is a computer generated attendance record</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // Memoized image component to prevent re-renders (same as PayrollLeave)
  const EmployeeImage = useMemo(() => {
    // Inline SVG placeholder (40x40 circle) eliminates network 404 loops
    const FALLBACK_DATA_URL =
      'data:image/svg+xml;utf8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <rect width="40" height="40" fill="#f0f0f0"/>
          <circle cx="20" cy="15" r="8" fill="#c0c0c0"/>
          <rect x="10" y="26" width="20" height="8" rx="4" fill="#d0d0d0"/>
        </svg>`
      );

    return ({ src, alt, size = 40 }) => (
      <img
        src={src || FALLBACK_DATA_URL}
        alt={alt}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          objectFit: 'cover',
          border: '1px solid #e0e0e0'
        }}
        onError={(e) => {
          // Prevent infinite onError loop by removing handler, then set fallback
          e.currentTarget.onerror = null;
          e.currentTarget.src = FALLBACK_DATA_URL;
        }}
      />
    );
  }, []);

  // Load base employee list once
  useEffect(() => {
    fetchEmployeeList();
  }, []);

  // Auto-fetch whenever filters change so list updates immediately
  useEffect(() => {
    fetchAttendanceData();
  }, [filters.fromDate, filters.toDate, filters.employeeId, filters.status]);

  const fetchEmployeeList = async () => {
    try {
      const response = await axios.get('/payroll/attendance/test-employees');
      if (response.data && response.data.success) {
        const list = response.data.sampleEmployees || [];
        setEmployeeList(list);
        console.log('👥 Loaded', list.length, 'employees for filter (from test-employees)');
      }
    } catch (err) {
      console.error('❌ Error loading employee list:', err);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      setLoading(true);
      
      console.log('🔍 Applying filters:', filters);
      
      const params = {
        fromDate: filters.fromDate.format("YYYY-MM-DD"),
        toDate: filters.toDate.format("YYYY-MM-DD"),
      };

      // Only add employeeId if a specific employee is selected (not empty string)
      if (filters.employeeId && filters.employeeId !== '') {
        params.employeeId = filters.employeeId;
        console.log('👤 Filtering by employee:', filters.employeeId);
      } else {
        console.log('👥 Showing all employees');
      }

      // Only add status if not 'all'
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
        console.log('🏷️ Filtering by status:', filters.status);
      }

      // Try to fetch real data from backend
      const response = await axios.get("/payroll/attendance", { params });

      if (response.data && response.data.success) {
        // Backend returned real data
        console.log("✅ Real attendance data loaded:", response.data.list.length, "records");

        // Derive employees from attendance list as a fallback / enrichment
        try {
          const derivedEmployeesMap = new Map();
          (response.data.list || []).forEach((r) => {
            if (r.employeeId) {
              const key = r.employeeId;
              if (!derivedEmployeesMap.has(key)) {
                derivedEmployeesMap.set(key, {
                  _id: r.employeeId,
                  emp_id: r.empCode || r.employeeId,
                  staff_name: r.employeeName || 'Unknown',
                  department: r.department || 'N/A',
                });
              }
            }
          });

          if (derivedEmployeesMap.size > 0) {
            const derivedList = Array.from(derivedEmployeesMap.values());
            // Merge with existing employeeList without duplicates
            setEmployeeList((prev) => {
              const existingMap = new Map(prev.map((e) => [e._id, e]));
              derivedList.forEach((e) => {
                if (!existingMap.has(e._id)) {
                  existingMap.set(e._id, e);
                }
              });
              const merged = Array.from(existingMap.values());
              console.log('👥 Employee list merged from attendance:', merged.length);
              return merged;
            });
          }
        } catch (mergeErr) {
          console.error('⚠️ Error deriving employees from attendance list:', mergeErr);
        }

        // Show message if no employees found
        if (response.data.message) {
          console.log("💬 Backend message:", response.data.message);
          Swal.fire({
            icon: 'info',
            title: 'No Employees Found',
            html: `
              <p>${response.data.message}</p>
              <p style="margin-top: 10px; color: #666;">
                Please add employees in the <strong>HRM → Employees</strong> module first.
              </p>
            `,
            confirmButtonText: 'OK'
          });
        }
        
        setAttendanceData(response.data.list || []);
        setStats(response.data.stats || {
          totalRecords: response.data.list?.length || 0,
          present: 0,
          absent: 0,
          late: 0,
          totalHours: 0
        });
      } else {
        // Backend returned empty or invalid data
        console.log("⚠️ No real data, using mock data");
        const mockData = generateMockData();
        setAttendanceData(mockData);
        setStats({
          totalRecords: mockData.length,
          present: mockData.filter(r => r.status === "On Time").length,
          absent: 0,
          late: mockData.filter(r => r.status === "Late").length,
          totalHours: mockData.reduce((sum, r) => sum + parseFloat(r.duration), 0)
        });
      }
    } catch (err) {
      console.error("❌ Error fetching attendance:", err.message);
      // Use mock data on error
      console.log("⚠️ API error, using mock data");
      const mockData = generateMockData();
      setAttendanceData(mockData);
      setStats({
        totalRecords: mockData.length,
        present: mockData.filter(r => r.status === "On Time").length,
        absent: 0,
        late: mockData.filter(r => r.status === "Late").length,
        totalHours: mockData.reduce((sum, r) => sum + parseFloat(r.duration), 0)
      });
    } finally {
      setLoading(false);
    }
  };

  // Generate mock data for demonstration
  const generateMockData = () => {
    const employees = [
      "John Doe",
      "Jane Smith",
      "Mike Johnson",
      "Sarah Williams",
      "David Brown",
    ];
    const data = [];
    const startDate = filters.fromDate;
    const endDate = filters.toDate;

    for (let d = startDate; d.isBefore(endDate) || d.isSame(endDate); d = d.add(1, "day")) {
      employees.forEach((name, idx) => {
        const checkIn = d
          .hour(8 + Math.floor(Math.random() * 2))
          .minute(Math.floor(Math.random() * 60));
        const checkOut = checkIn.add(8 + Math.floor(Math.random() * 2), "hour");
        const duration = checkOut.diff(checkIn, "hour", true);

        data.push({
          _id: `${d.format("YYYYMMDD")}-${idx}`,
          employeeId: `EMP${100 + idx}`,
          employeeName: name,
          date: d.format("YYYY-MM-DD"),
          checkIn: checkIn.format("YYYY-MM-DD HH:mm:ss"),
          checkOut: checkOut.format("YYYY-MM-DD HH:mm:ss"),
          duration: duration.toFixed(2),
          status: checkIn.hour() > 9 ? "Late" : "On Time",
          department: ["IT", "HR", "Finance", "Operations", "Sales"][idx],
        });
      });
    }

    return data.slice(0, 50); // Return first 50 records
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("fromDate", filters.fromDate.format("YYYY-MM-DD"));
      formData.append("toDate", filters.toDate.format("YYYY-MM-DD"));

      // Try to upload to backend
      const response = await axios.post("/payroll/attendance/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data && response.data.success) {
        // Real upload successful
        console.log("✅ File uploaded successfully:", response.data);
        Swal.fire({
          icon: "success",
          title: "Upload Successful!",
          html: `
            <p>Records Processed: <strong>${response.data.recordsProcessed || 0}</strong></p>
            <p>Records Failed: <strong>${response.data.recordsFailed || 0}</strong></p>
            ${response.data.errors?.length > 0 ? `<p style="color: red;">Errors: ${response.data.errors.slice(0, 3).join(", ")}</p>` : ""}
          `,
        });
      } else {
        throw new Error("Upload failed");
      }

      setUploadDialogOpen(false);
      setSelectedFile(null);
      fetchAttendanceData();
    } catch (err) {
      console.error("❌ Error uploading file:", err.message);
      // Mock success for demo
      Swal.fire({
        icon: "info",
        title: "Mock Upload",
        text: "Backend not connected. File would be uploaded in production.",
      });
      setUploadDialogOpen(false);
      setSelectedFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
  };

  const handleEditAttendance = (record) => {
    setEditingRecord(record);
    
    // Pre-fill form with existing data
    const checkInDate = record.checkIn ? dayjs(record.checkIn).format('YYYY-MM-DDTHH:mm') : '';
    const checkOutDate = record.checkOut ? dayjs(record.checkOut).format('YYYY-MM-DDTHH:mm') : '';
    
    setEditForm({
      checkIn: checkInDate,
      checkOut: checkOutDate,
      notes: record.notes || ''
    });
    
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingRecord) return;
    
    try {
      setUploading(true);

      // Prevent future dates/times for check-in/check-out
      const now = new Date();
      const checkInLocal = editForm.checkIn ? new Date(editForm.checkIn) : null;
      const checkOutLocal = editForm.checkOut ? new Date(editForm.checkOut) : null;

      if (checkInLocal && checkInLocal > now) {
        setUploading(false);
        Swal.fire({
          icon: 'warning',
          title: 'Invalid Check In',
          text: 'Check-in time cannot be in the future.',
        });
        return;
      }

      if (checkOutLocal && checkOutLocal > now) {
        setUploading(false);
        Swal.fire({
          icon: 'warning',
          title: 'Invalid Check Out',
          text: 'Check-out time cannot be in the future.',
        });
        return;
      }
      
      const payload = {
        employeeId: editingRecord.employeeId,
        date: dayjs(editingRecord.date).format('YYYY-MM-DD'),
        checkIn: editForm.checkIn ? new Date(editForm.checkIn).toISOString() : null,
        checkOut: editForm.checkOut ? new Date(editForm.checkOut).toISOString() : null,
        notes: editForm.notes
      };
      
      const response = await axios.post('/payroll/attendance/manual-edit', payload);
      
      if (response.data && response.data.success) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: response.data.message || 'Attendance updated successfully',
          timer: 2000
        });
        
        setEditDialogOpen(false);
        setEditingRecord(null);
        setEditForm({ checkIn: '', checkOut: '', notes: '' });
        
        // Refresh data
        fetchAttendanceData();
      } else {
        throw new Error(response.data?.message || 'Update failed');
      }
    } catch (err) {
      console.error('❌ Error updating attendance:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || err.message || 'Failed to update attendance'
      });
    } finally {
      setUploading(false);
    }
  };


  const handleExport = () => {
    // Export functionality
    const csv = [
      ["Employee ID", "Employee Name", "Date", "Check In", "Check Out", "Duration", "Status"],
      ...attendanceData.map((r) => [
        r.employeeId,
        r.employeeName,
        r.date,
        r.checkIn,
        r.checkOut,
        r.duration,
        r.status,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_${dayjs().format("YYYY-MM-DD")}.csv`;
    a.click();
  };

  const formatDuration = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "On Time":
        return "success";
      case "Late":
        return "warning";
      case "Absent":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Attendance Payroll</Typography>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<FilterIcon />}
              onClick={fetchAttendanceData}
              disabled={loading}
            >
              Apply Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setFilters({
                  fromDate: dayjs().startOf("month"),
                  toDate: dayjs().endOf("month"),
                  employeeId: "",
                  status: "all",
                });
                setTimeout(() => fetchAttendanceData(), 100);
              }}
              disabled={loading}
            >
              Reset Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={attendanceData.length === 0}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintList}
              disabled={attendanceData.length === 0}
            >
              Print List
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={() => setUploadDialogOpen(true)}
            >
              Upload Attendance
            </Button>
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Records
                    </Typography>
                    <Typography variant="h5">{stats.totalRecords}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: "success.main" }}>
                    <CheckInIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Present
                    </Typography>
                    <Typography variant="h5">{stats.present}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: "error.main" }}>
                    <CheckOutIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Absent
                    </Typography>
                    <Typography variant="h5">{stats.absent}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: "warning.main" }}>
                    <CalendarIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Late
                    </Typography>
                    <Typography variant="h5">{stats.late}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <Avatar sx={{ bgcolor: "info.main" }}>
                    <DurationIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Hours
                    </Typography>
                    <Typography variant="h5">
                      {Number(stats.totalHours || 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              {/* From Date - a bit smaller to give Employee more space */}
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="From Date"
                  value={filters.fromDate}
                  onChange={(date) => setFilters({ ...filters, fromDate: date })}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Grid>
              {/* To Date */}
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="To Date"
                  value={filters.toDate}
                  onChange={(date) => setFilters({ ...filters, toDate: date })}
                  format="DD/MM/YYYY"
                  slotProps={{ textField: { fullWidth: true, size: "small" } }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  fullWidth
                  size="small"
                  sx={{ minWidth: 320 }}
                  options={[{ _id: '', emp_id: 'All', staff_name: 'All Employees' }, ...employeeList]}
                  getOptionLabel={(option) => 
                    option._id === '' 
                      ? 'All Employees' 
                      : `${option.emp_id} - ${option.staff_name}`
                  }
                  value={employeeList.find(emp => emp._id === filters.employeeId) || { _id: '', emp_id: 'All', staff_name: 'All Employees' }}
                  onChange={(event, newValue) => {
                    setFilters({ ...filters, employeeId: newValue?._id || '' });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Employee" placeholder="Search employee..." />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option._id}>
                      {option._id === '' ? (
                        <strong>All Employees</strong>
                      ) : (
                        <span>{option.emp_id} - {option.staff_name}</span>
                      )}
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    label="Status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  >
                    <MenuItem value="all"><strong>All Status</strong></MenuItem>
                    <MenuItem value="present">Present</MenuItem>
                    <MenuItem value="absent">Absent</MenuItem>
                    <MenuItem value="late">Late</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card>
          <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
            <Tab label="Table View" />
            <Tab label="Card View" />
          </Tabs>
          <Divider />

          {loading ? (
            <Box display="flex" justifyContent="center" p={5}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Table View */}
              {tabValue === 0 && (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Image</TableCell>
                        <TableCell>Emp Code</TableCell>
                        <TableCell>Employee Name</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Check In</TableCell>
                        <TableCell>Check Out</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendanceData.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            <Box py={5}>
                              <PersonIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                              <Typography variant="h6" color="text.secondary" gutterBottom>
                                No Employees Found
                              </Typography>
                              <Typography color="text.secondary" variant="body2">
                                Please add employees in HRM → Employees module first
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        attendanceData.map((record, idx) => (
                          <TableRow key={record._id} hover>
                            <TableCell>
                              {(() => {
                                const rawImg =
                                  record.imageUrl ||
                                  record.profileImage ||
                                  record.image ||
                                  record.employeePhoto ||
                                  "";
                                const base = axios.defaults.baseURL || "";
                                const imageUrl = rawImg && rawImg !== base
                                  ? (rawImg.startsWith("http") ? rawImg : `${base}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`)
                                  : null;
                                if (idx === 0) {
                                  console.log("AttendancePayroll (table) image base:", base, "raw:", rawImg, "final:", imageUrl);
                                }
                                return (
                                  <EmployeeImage src={imageUrl} alt={record.employeeName || 'Employee'} size={40} />
                                );
                              })()}
                            </TableCell>
                            <TableCell>{record.empCode || record.employeeId}</TableCell>
                            <TableCell>{record.employeeName}</TableCell>
                            <TableCell>{dayjs(record.date).format("DD/MMM/YYYY")}</TableCell>
                            <TableCell>
                              {record.checkIn ? dayjs(record.checkIn).format("hh:mm A") : '-'}
                            </TableCell>
                            <TableCell>
                              {record.checkOut ? dayjs(record.checkOut).format("hh:mm A") : '-'}
                            </TableCell>
                            <TableCell>{record.duration > 0 ? formatDuration(record.duration) : '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={record.status}
                                color={getStatusColor(record.status)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={record.source || 'N/A'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={() => handleEditAttendance(record)}
                                sx={{ mr: 1 }}
                                title="Edit Attendance"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetails(record)}
                                sx={{ mr: 1 }}
                                title="View Details"
                              >
                                <ViewIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedRecord(record);
                                  handlePrintSingle();
                                }}
                                title="Print"
                              >
                                <PrintIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Card View */}
              {tabValue === 1 && (
                <Box p={2}>
                  {attendanceData.length === 0 ? (
                    <Typography color="text.secondary" align="center" py={3}>
                      No attendance records found
                    </Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {attendanceData.map((record, idx) => (
                        <Grid item xs={12} sm={6} md={4} key={record._id}>
                          <Card variant="outlined">
                            <CardContent>
                              <Box display="flex" alignItems="center" mb={2}>
                                {(() => {
                                  const rawImg =
                                    record.imageUrl ||
                                    record.profileImage ||
                                    record.image ||
                                    record.employeePhoto ||
                                    "";
                                  const base = axios.defaults.baseURL || "";
                                  const imageUrl = rawImg && rawImg !== base
                                    ? (rawImg.startsWith("http") ? rawImg : `${base}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`)
                                    : null;
                                  if (idx === 0) {
                                    console.log("AttendancePayroll (card) image base:", base, "raw:", rawImg, "final:", imageUrl);
                                  }
                                  return (
                                    <EmployeeImage src={imageUrl} alt={record.employeeName || 'Employee'} size={48} />
                                  );
                                })()}
                                <Box ml={2} flex={1}>
                                  <Typography variant="h6">{record.employeeName}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {record.employeeId} • {record.department}
                                  </Typography>
                                </Box>
                                <Chip
                                  label={record.status}
                                  color={getStatusColor(record.status)}
                                  size="small"
                                />
                              </Box>
                              <Divider sx={{ my: 1 }} />
                              <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2">Date:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {dayjs(record.date).format("MMM DD, YYYY")}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2">Check In:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {dayjs(record.checkIn).format("hh:mm A")}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between" mb={1}>
                                <Typography variant="body2">Check Out:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                  {dayjs(record.checkOut).format("hh:mm A")}
                                </Typography>
                              </Box>
                              <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">Duration:</Typography>
                                <Typography variant="body2" fontWeight="bold" color="primary">
                                  {formatDuration(record.duration)}
                                </Typography>
                              </Box>
                              <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                startIcon={<ViewIcon />}
                                onClick={() => handleViewDetails(record)}
                                sx={{ mt: 2 }}
                              >
                                View Details
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Box>
              )}
            </>
          )}
        </Card>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload Attendance File</DialogTitle>
          <DialogContent>
            <Box py={2}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Upload CSV or Excel file with columns: Employee ID, Date, Check In, Check Out
              </Alert>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                style={{ display: "none" }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<UploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Choose File
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" color="text.secondary">
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleFileUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? <CircularProgress size={24} /> : "Upload"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* View Details Dialog */}
        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Attendance Details</DialogTitle>
          <DialogContent>
            {selectedRecord && (
              <Box py={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Employee ID
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedRecord.employeeId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedRecord.employeeName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Department
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {selectedRecord.department}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {dayjs(selectedRecord.date).format("MMM DD, YYYY")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Check In
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {dayjs(selectedRecord.checkIn).format("hh:mm A")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Check Out
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {dayjs(selectedRecord.checkOut).format("hh:mm A")}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Duration
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatDuration(selectedRecord.duration)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={selectedRecord.status}
                      color={getStatusColor(selectedRecord.status)}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintSingle}
              disabled={!selectedRecord}
            >
              Print
            </Button>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Edit Attendance Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Edit Attendance - {editingRecord?.employeeName}
          </DialogTitle>
          <DialogContent>
            {editingRecord && (
              <Box py={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Employee: <strong>{editingRecord.employeeName}</strong> ({editingRecord.empCode})<br />
                      Date: <strong>{dayjs(editingRecord.date).format("MMM DD, YYYY")}</strong><br />
                      Current Status: <strong>{editingRecord.status}</strong>
                    </Alert>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Check In"
                      type="datetime-local"
                      value={editForm.checkIn}
                      onChange={(e) => setEditForm({ ...editForm, checkIn: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Check Out"
                      type="datetime-local"
                      value={editForm.checkOut}
                      onChange={(e) => setEditForm({ ...editForm, checkOut: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      value={editForm.notes}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      placeholder="Add notes about this manual edit..."
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Alert severity="warning">
                      <strong>Note:</strong> Manual edits will be marked with source "manual" and will override any device-synced data.
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={uploading || !editForm.checkIn}
            >
              {uploading ? <CircularProgress size={24} /> : "Save Changes"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AttendancePayroll;
